import type {
  CapitalGlobalAddressFieldNames,
  CapitalGlobalProposalAddressFieldPrefix,
} from '@/features/proposals/components/capital-global/constants/capital-global-proposal-address-field-names.constant';
import { CAPITAL_GLOBAL_PROPOSAL_ADDRESS_FIELD_NAMES } from '@/features/proposals/components/capital-global/constants/capital-global-proposal-address-field-names.constant';
import type { CapitalGlobalProposalStep1FormApi } from '@/features/proposals/components/capital-global/steps/capital-global-proposal-step1-form.types';
import type { CapitalGlobalStep1FieldApiLike } from '@/features/proposals/components/capital-global/utils/capital-global-step1-should-show-field-errors.util';
import { BRAZIL_UF_SELECT_OPTIONS } from '@/features/proposals/components/capital-global/constants/brazil-uf-select-options.constant';
import { FIELD_MAX_LENGTHS } from '@/shared/constants';
import type { MutableRefObject } from 'react';

import type { DeepKeys } from '@tanstack/react-form';
import type { CapitalGlobalProposalStepDadosDoSeguroValues } from '@/features/proposals/schemas/capital-global/proposal-step-dados-do-seguro.schema';

type FieldName = DeepKeys<CapitalGlobalProposalStepDadosDoSeguroValues>;

type Props = {
  form: CapitalGlobalProposalStep1FormApi;
  /** Bloco fixo do estipulante. Mutuamente exclusivo com `fieldNames`. */
  fieldPrefix?: CapitalGlobalProposalAddressFieldPrefix;
  /** Nomes explícitos — usado pelos subestipulantes (`subStipulatorAddresses[i].*`). */
  fieldNames?: CapitalGlobalAddressFieldNames;
  isViewMode: boolean;
  isLoadingCep: boolean;
  hasResolvedCep: boolean;
  canEditStreetNeighborhood: boolean;
  zipcodeLookupCacheRef: MutableRefObject<Record<string, boolean>>;
  fetchAndFill: (
    rawCep: string,
    options?: { onlyEmpty?: boolean }
  ) => Promise<void>;
  shouldShowStep1FieldErrors: (
    fieldApi: CapitalGlobalStep1FieldApiLike
  ) => boolean;
  /** Quando true, exibe logradouro e demais campos só após CEP resolvido (correspondência). */
  hideUntilCepResolved?: boolean;
};

export function CapitalGlobalProposalAddressFields({
  form,
  fieldPrefix,
  fieldNames,
  isViewMode,
  isLoadingCep,
  hasResolvedCep,
  canEditStreetNeighborhood,
  zipcodeLookupCacheRef,
  fetchAndFill,
  shouldShowStep1FieldErrors,
  hideUntilCepResolved = false,
}: Props) {
  const fields =
    fieldNames ??
    CAPITAL_GLOBAL_PROPOSAL_ADDRESS_FIELD_NAMES[fieldPrefix ?? 'registration'];
  const showAddressDetails = !hideUntilCepResolved || hasResolvedCep;

  // Lê pelo path (funciona igual para chave plana e acessor de array) — antes esses
  // valores vinham de um if hardcoded nos dois prefixos, o que não escala para N subs.
  const streetValue = form.getFieldValue(fields.street as FieldName) as
    | string
    | undefined;
  const neighborhoodValue = form.getFieldValue(
    fields.neighborhood as FieldName
  ) as string | undefined;

  return (
    <div className='flex flex-col gap-4'>
      <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
        <form.AppField
          name={fields.cep as FieldName}
          validators={{
            onChange: ({ value, fieldApi }) => {
              if (!shouldShowStep1FieldErrors(fieldApi)) return undefined;
              const s = String(value || '').trim();
              if (!s) return 'CEP é obrigatório';
              const clean = s.replace(/\D/g, '');
              if (clean.length !== 8) return 'CEP deve ter 8 dígitos';
              if (zipcodeLookupCacheRef.current[clean] === false) {
                return 'CEP inválido ou não encontrado';
              }
              return undefined;
            },
          }}
          children={field => (
            <field.TextField
              label='CEP'
              placeholder='XXXXX-XXX'
              required
              maxLength={FIELD_MAX_LENGTHS.cep}
              mask='99999-999'
              disabled={isViewMode || isLoadingCep}
              onBlur={async () => {
                const raw = form.getFieldValue(fields.cep as FieldName);
                const clean = String(raw || '').replace(/\D/g, '');
                if (clean.length !== 8) return;
                await fetchAndFill(String(raw || ''));
                await form.validateField(fields.cep as FieldName, 'change');
              }}
            />
          )}
        />
      </div>

      {showAddressDetails ? (
        <>
          <form.AppField
            name={fields.street as FieldName}
            validators={{
              onChange: ({ value, fieldApi }) => {
                if (!shouldShowStep1FieldErrors(fieldApi)) return undefined;
                if (!String(value || '').trim()) return 'Endereço é obrigatório';
                return undefined;
              },
            }}
            children={field => (
              <field.TextField
                label='Endereço'
                placeholder='Rua, Avenida ...'
                required
                // i4Pro `I4ProAddress.street` é @MaxLength(150) — sem isso o upsert de
                // pessoa falha só na emissão.
                maxLength={FIELD_MAX_LENGTHS.street}
                disabled={
                  isViewMode ||
                  (!canEditStreetNeighborhood && !!streetValue)
                }
              />
            )}
          />

          <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
            <form.AppField
              name={fields.number as FieldName}
              validators={{
                onChange: ({ value, fieldApi }) => {
                  if (!shouldShowStep1FieldErrors(fieldApi)) return undefined;
                  if (!String(value || '').trim()) return 'Número é obrigatório';
                  return undefined;
                },
              }}
              children={field => (
                <field.TextField
                  label='Número'
                  placeholder='Número'
                  required
                  maxLength={FIELD_MAX_LENGTHS.addressNumber}
                  disabled={isViewMode}
                />
              )}
            />
            <form.AppField
              name={fields.complement as FieldName}
              children={field => (
                <field.TextField
                  label='Complemento'
                  placeholder='Apto, Bloco ...'
                  maxLength={FIELD_MAX_LENGTHS.complement}
                  disabled={isViewMode}
                />
              )}
            />
          </div>

          <div className='grid grid-cols-1 lg:grid-cols-3 gap-4'>
            <form.AppField
              name={fields.neighborhood as FieldName}
              validators={{
                onChange: ({ value, fieldApi }) => {
                  if (!shouldShowStep1FieldErrors(fieldApi)) return undefined;
                  if (!String(value || '').trim()) return 'Bairro é obrigatório';
                  return undefined;
                },
              }}
              children={field => (
                <field.TextField
                  label='Bairro'
                  placeholder='Bairro'
                  required
                  maxLength={FIELD_MAX_LENGTHS.neighborhood}
                  disabled={
                    isViewMode ||
                    (!canEditStreetNeighborhood && !!neighborhoodValue)
                  }
                />
              )}
            />
            <form.AppField
              name={fields.city as FieldName}
              validators={{
                onChange: ({ value, fieldApi }) => {
                  if (!shouldShowStep1FieldErrors(fieldApi)) return undefined;
                  if (!String(value || '').trim()) return 'Cidade é obrigatória';
                  return undefined;
                },
              }}
              children={field => (
                <field.TextField
                  label='Cidade'
                  placeholder='Cidade'
                  required
                  maxLength={FIELD_MAX_LENGTHS.city}
                  disabled
                />
              )}
            />
            <form.AppField
              name={fields.state as FieldName}
              validators={{
                onChange: ({ value, fieldApi }) => {
                  if (!shouldShowStep1FieldErrors(fieldApi)) return undefined;
                  if (!String(value || '').trim()) return 'UF é obrigatória';
                  return undefined;
                },
              }}
              children={field => (
                <field.SelectField
                  label='Estado'
                  placeholder='UF'
                  required
                  options={BRAZIL_UF_SELECT_OPTIONS}
                  disabled
                />
              )}
            />
          </div>
        </>
      ) : null}
    </div>
  );
}
